import type { TransferRecommendationQuery } from './recommendations';
import type { components } from '../types/api/generated';

/**
 * The trigger's JSON body.
 *
 * Every field is optional: the backend defaults anything omitted, and the
 * generated type marks the defaulted fields required only because a default
 * makes them certain on the way out, not on the way in.
 */
export type TransferRunRequestBody = Partial<
  components['schemas']['TransferRunRequest']
>;

const LIST_FIELDS = [
  'teamIds',
  'playerIdsIn',
  'playerIdsOut',
  'excludePlayerIds'
] as const;

type ListField = (typeof LIST_FIELDS)[number];

const splitCommaSeparated = (value: string): string[] =>
  value.split(',').filter(Boolean);

/**
 * Encodes a transfer request as the trigger's JSON body.
 *
 * Callers still describe a request in the synchronous endpoint's query shape,
 * because that endpoint is still the fallback and has to be given the same
 * request. This is the one place the two encodings differ, so it is the one
 * place they can disagree.
 *
 * Both feed the same solve cache key on the backend, and a field defaulted
 * differently on either side computes a different key for the same request:
 * no error, just the same work bought twice. So the rule here is to change the
 * encoding and nothing else. A value the caller did not supply is left out
 * rather than given a default this side, and a null is left out too, exactly
 * as the query serialiser drops it today.
 */
export const toTransferRunRequestBody = (
  query: TransferRecommendationQuery
): TransferRunRequestBody => {
  const body: Record<string, unknown> = {};

  for (const [field, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;

    // Lists are genuinely lists here. The query string could only carry them
    // comma-joined, which is what the body exists to stop.
    if (LIST_FIELDS.includes(field as ListField)) {
      const members = splitCommaSeparated(value as string);
      if (members.length > 0) body[field] = members;
      continue;
    }

    // A boolean, rather than the two strings that spell one on a query string.
    if (field === 'isInPool') {
      body[field] = value === 'true';
      continue;
    }

    body[field] = value;
  }

  return body as TransferRunRequestBody;
};
